import React, { useState, useEffect } from "react";
import {
  X,
  Send,
  Copy,
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  RotateCw,
  Clock,
  Star,
  ShieldCheck,
} from "lucide-react";
import type { SocialQueueItem, SocialReplyPublishResponse } from "../../types";
import { suggestSocialReply, publishSocialReply } from "../../api";
import { formatDateTime, stripGoogleTranslation } from "./socialFormat";
import { SocialBadge } from "./SocialBadge";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: SocialQueueItem | null;
  clientSlug: string;
  onReplySuccess?: (feedbackId: string, method: string, approvedText: string) => void;
}

type ToneType = "cordial" | "empatico" | "directo" | "institucional";

const PLATFORM_CONFIG: Record<
  string,
  { name: string; color: string; maxChars: number; defaultUrl: string }
> = {
  gbp: {
    name: "Google BP",
    color: "#34a853",
    maxChars: 4096,
    defaultUrl: "https://www.google.com/maps",
  },
  meta_ig: {
    name: "Instagram",
    color: "#e1306c",
    maxChars: 2200,
    defaultUrl: "https://www.instagram.com",
  },
  meta_fb: {
    name: "Facebook",
    color: "#1877f2",
    maxChars: 8000,
    defaultUrl: "https://www.facebook.com",
  },
};

export function SocialReplyModal({
  isOpen,
  onClose,
  item,
  clientSlug,
  onReplySuccess,
}: Props) {
  const [tone, setTone] = useState<ToneType>("cordial");
  const [draftText, setDraftText] = useState("");
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedSuccess, setCopiedSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "info";
    text: string;
    canFallbackManual?: boolean;
  } | null>(null);

  // Inicializar borrador al abrir modal
  useEffect(() => {
    if (isOpen && item) {
      setTone("cordial");
      setStatusMessage(null);
      setCopiedSuccess(false);

      // Si el item ya traía un borrador precalculado, usarlo
      if (item.plan.draft_text) {
        setDraftText(item.plan.draft_text);
      } else {
        // Solicitar sugerencia al backend
        loadSuggestion("cordial");
      }
    }
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  const platKey = item.platform.toLowerCase();
  const platConfig =
    PLATFORM_CONFIG[platKey] ||
    (platKey.includes("fb")
      ? PLATFORM_CONFIG.meta_fb
      : platKey.includes("ig")
      ? PLATFORM_CONFIG.meta_ig
      : PLATFORM_CONFIG.gbp);

  const cleanMessage =
    platKey === "gbp"
      ? stripGoogleTranslation(item.message || "")
      : item.message || "";
  const rating =
    item.star_rating !== null && item.star_rating !== undefined
      ? Math.round(item.star_rating)
      : null;
  const isEscalate =
    item.plan.action === "escalate" || item.plan.priority === 1;
  const charCount = draftText.length;
  const isOverLimit = charCount > platConfig.maxChars;

  const loadSuggestion = async (selectedTone: ToneType) => {
    if (!item) return;
    setLoadingSuggestion(true);
    setStatusMessage(null);
    try {
      const res = await suggestSocialReply({
        client_slug: clientSlug,
        feedback_id: item.feedback_id,
        tone: selectedTone,
      });
      if (res.draft_text) {
        setDraftText(res.draft_text);
      }
    } catch (err: any) {
      console.warn("Error cargando sugerencia:", err);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleToneChange = (newTone: ToneType) => {
    setTone(newTone);
    loadSuggestion(newTone);
  };

  const handlePublishDirectApi = async () => {
    if (!draftText.trim()) {
      setStatusMessage({
        type: "error",
        text: "La respuesta no puede estar vacía.",
      });
      return;
    }
    if (isOverLimit) {
      setStatusMessage({
        type: "error",
        text: `El texto supera el límite máximo de ${platConfig.maxChars} caracteres permitido por ${platConfig.name}.`,
      });
      return;
    }

    setSubmitting(true);
    setStatusMessage(null);
    try {
      const res: SocialReplyPublishResponse = await publishSocialReply({
        client_slug: clientSlug,
        feedback_id: item.feedback_id,
        approved_text: draftText,
        action_type: "direct_api",
        platform: item.platform,
        post_id: item.post_id,
        page_id: item.page_id,
        ad_account_id: item.ad_account_id,
      });

      if (res.success) {
        setStatusMessage({
          type: "success",
          text: "✓ Respuesta aprobada y publicada exitosamente en la plataforma.",
        });
        if (onReplySuccess) {
          onReplySuccess(item.feedback_id, "direct_api", draftText);
        }
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        setStatusMessage({
          type: "error",
          text:
            res.api_error ||
            "Error al publicar vía API. Es posible que el token no tenga permisos de escritura.",
          canFallbackManual: true,
        });
      }
    } catch (err: any) {
      setStatusMessage({
        type: "error",
        text:
          err.message ||
          "Error de comunicación con el servidor al enviar respuesta.",
        canFallbackManual: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualCopied = async () => {
    if (!draftText.trim()) return;

    // 1. Copiar al portapapeles
    try {
      await navigator.clipboard.writeText(draftText);
      setCopiedSuccess(true);
    } catch {
      /* fallback silencioso */
    }

    // 2. Abrir plataforma en nueva pestaña
    const targetUrl = item.action_link?.href || platConfig.defaultUrl;
    window.open(targetUrl, "_blank", "noopener,noreferrer");

    // 3. Registrar en backend como respuesta manual aprobada
    try {
      await publishSocialReply({
        client_slug: clientSlug,
        feedback_id: item.feedback_id,
        approved_text: draftText,
        action_type: "manual_copied",
        platform: item.platform,
        post_id: item.post_id,
        page_id: item.page_id,
        ad_account_id: item.ad_account_id,
      });
      if (onReplySuccess) {
        onReplySuccess(item.feedback_id, "manual_copied", draftText);
      }
      setStatusMessage({
        type: "success",
        text: "Texto copiado al portapapeles y registrado en auditoría.",
      });
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.warn("Error registrando auditoría manual:", err);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface, #11141b)",
          border: "1px solid var(--line, rgba(255, 255, 255, 0.1))",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "680px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.8)",
          overflow: "hidden",
        }}
      >
        {/* Cabecera del modal */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: platConfig.color,
                display: "inline-block",
              }}
            />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "var(--text, #f6f7fb)",
                  }}
                >
                  Aprobación Humana de Respuesta
                </h3>
                <SocialBadge tone="info">{platConfig.name}</SocialBadge>
              </div>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "12px",
                  color: "var(--muted, #8e96a8)",
                }}
              >
                Supervisión humana obligatoria: revisa, ajusta y autoriza el envío.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="button button-ghost"
            style={{
              padding: "6px",
              borderRadius: "8px",
              color: "var(--muted, #8e96a8)",
            }}
            onClick={onClose}
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        {/* Cuerpo del modal (scrollable) */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          {/* Tarjeta del comentario original */}
          <div
            style={{
              background: "var(--surface-2, #151923)",
              border: "1px solid var(--line, rgba(255, 255, 255, 0.06))",
              borderRadius: "10px",
              padding: "14px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "12px",
                color: "var(--muted, #8e96a8)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: 600, color: "var(--text, #f6f7fb)" }}>
                  {item.author_name || "Usuario"}
                </span>
                <span>·</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={12} />
                  {formatDateTime(item.created_time || item.date)}
                </span>
                {rating !== null && rating > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", color: "#f59e0b" }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={11}
                        fill={s <= rating ? "#f59e0b" : "none"}
                        stroke={s <= rating ? "#f59e0b" : "#64748b"}
                      />
                    ))}
                    <span style={{ fontWeight: 600, marginLeft: "2px" }}>{rating}★</span>
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {item.polaridad && (
                  <SocialBadge
                    tone={
                      item.polaridad === "negativa"
                        ? "danger"
                        : item.polaridad === "positiva"
                        ? "success"
                        : "neutral"
                    }
                  >
                    {item.polaridad}
                  </SocialBadge>
                )}
                {item.acto_de_habla && (
                  <SocialBadge tone="neutral">{item.acto_de_habla}</SocialBadge>
                )}
              </div>
            </div>

            <div
              style={{
                fontSize: "13px",
                lineHeight: "1.5",
                color: "var(--text, #f6f7fb)",
                padding: "8px 12px",
                background: "rgba(0, 0, 0, 0.25)",
                borderRadius: "6px",
                borderLeft: isEscalate
                  ? "3px solid #ff665e"
                  : "3px solid var(--blue, #7d96ff)",
              }}
            >
              {cleanMessage || "Sin texto"}
            </div>
          </div>

          {/* Banner si el comentario ya fue respondido */}
          {Boolean(item.has_reply) && (
            <div
              style={{
                background: "rgba(75, 212, 160, 0.12)",
                border: "1px solid rgba(75, 212, 160, 0.3)",
                borderRadius: "8px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "12px",
                color: "#4bd4a0",
              }}
            >
              <CheckCircle size={16} style={{ flexShrink: 0 }} />
              <div>
                <strong>Este comentario ya fue respondido:</strong> Para evitar duplicados en la plataforma, el despacho automático vía API está deshabilitado. Puedes usar la opción de copiar y abrir si necesitas revisarlo directamente.
              </div>
            </div>
          )}

          {/* Banner de alerta de escalamiento si aplica */}
          {isEscalate && (
            <div
              style={{
                background: "rgba(255, 102, 94, 0.12)",
                border: "1px solid rgba(255, 102, 94, 0.3)",
                borderRadius: "8px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                fontSize: "12px",
                color: "#ff8a83",
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <strong>Atención prioritaria / Escalamiento:</strong>{" "}
                {item.plan.reason || "Caso con alta sensibilidad o riesgo potencial."}
                <div style={{ marginTop: "4px", color: "var(--text, #f6f7fb)", opacity: 0.9 }}>
                  Verifica que el tono y la derivación institucional cumplan el protocolo antes de autorizar.
                </div>
              </div>
            </div>
          )}

          {/* Área de sugerencia asistida */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "var(--text, #f6f7fb)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <ShieldCheck size={15} color="var(--blue, #7d96ff)" />
                Sugerencia de Respuesta
              </label>

              {/* Selector de tonos */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {(["cordial", "empatico", "directo", "institucional"] as ToneType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleToneChange(t)}
                    disabled={loadingSuggestion}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      background:
                        tone === t
                          ? "var(--blue, #7d96ff)"
                          : "rgba(255, 255, 255, 0.05)",
                      color: tone === t ? "#fff" : "var(--muted, #8e96a8)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => loadSuggestion(tone)}
                  title="Regenerar sugerencia"
                  disabled={loadingSuggestion}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: "var(--muted, #8e96a8)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <RotateCw
                    size={14}
                    className={loadingSuggestion ? "spin-animation" : ""}
                  />
                </button>
              </div>
            </div>

            {/* Textarea editable por el humano */}
            <div style={{ position: "relative" }}>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="Escribe o edita la respuesta antes de autorizarla..."
                rows={5}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: "var(--surface-2, #151923)",
                  border: isOverLimit
                    ? "1px solid #ff665e"
                    : "1px solid var(--line, rgba(255, 255, 255, 0.12))",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  color: "var(--text, #f6f7fb)",
                  resize: "vertical",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {/* Contador de caracteres y límite de la red */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "11px",
                color: isOverLimit ? "#ff665e" : "var(--dim, #5e6677)",
              }}
            >
              <span>
                {isOverLimit
                  ? `⚠️ Has superado el límite por ${charCount - platConfig.maxChars} caracteres.`
                  : "Texto 100% editable antes de enviar."}
              </span>
              <span style={{ fontWeight: 600 }}>
                {charCount} / {platConfig.maxChars} caracteres ({platConfig.name})
              </span>
            </div>
          </div>

          {/* Mensaje de estado / feedback */}
          {statusMessage && (
            <div
              style={{
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                background:
                  statusMessage.type === "success"
                    ? "rgba(75, 212, 160, 0.12)"
                    : "rgba(255, 102, 94, 0.12)",
                color:
                  statusMessage.type === "success" ? "#4bd4a0" : "#ff8a83",
                border:
                  statusMessage.type === "success"
                    ? "1px solid rgba(75, 212, 160, 0.3)"
                    : "1px solid rgba(255, 102, 94, 0.3)",
              }}
            >
              <div>{statusMessage.text}</div>
              {statusMessage.canFallbackManual && (
                <div style={{ marginTop: "4px" }}>
                  <button
                    type="button"
                    onClick={handleManualCopied}
                    className="button button-sm"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      color: "var(--text, #f6f7fb)",
                      border: "none",
                      fontSize: "11px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 10px",
                    }}
                  >
                    <Copy size={12} />
                    Copiar al portapapeles y abrir en la plataforma
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer del modal con acciones */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            background: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <button
            type="button"
            className="button button-ghost button-sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Opción 1: Copiar y Abrir en la plataforma (manual asistido sin riesgo de token) */}
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={handleManualCopied}
              disabled={submitting || !draftText.trim()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
              }}
              title="Copia el texto al portapapeles, abre la plataforma y registra en auditoría que respondiste manualmente"
            >
              {copiedSuccess ? <CheckCircle size={14} color="#4bd4a0" /> : <Copy size={14} />}
              Copiar y abrir en {platConfig.name.split(" ")[0]}
              <ExternalLink size={12} />
            </button>

            {/* Opción 2: Aprobar y Enviar directamente vía API */}
            <button
              type="button"
              className="button button-primary button-sm"
              onClick={handlePublishDirectApi}
              disabled={submitting || !draftText.trim() || isOverLimit || Boolean(item.has_reply)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 650,
                backgroundColor: item.has_reply ? "var(--dim, #5e6677)" : "var(--blue, #7d96ff)",
                opacity: item.has_reply ? 0.6 : 1,
                cursor: item.has_reply ? "not-allowed" : "pointer",
              }}
              title={item.has_reply ? "Este comentario ya cuenta con una respuesta registrada en la plataforma" : undefined}
            >
              <Send size={14} />
              {submitting ? "Enviando..." : Boolean(item.has_reply) ? "Ya Respondido (API)" : "Aprobar y Enviar (API)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
